import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { ADMIN_ADVERTISEMENT_PROMOTION_PATH } from "../../advertisement/advertisementRoutes";
import Logger from "../../../services/Logger";
import { CsvDownloadButton, csvNumber, downloadCsv } from "./adminCsvExport";
import {
    formatDurationMs,
    formatPlayTime,
    playDurationMs,
    promotionPlayTimeColumns,
} from "./promotionPlayColumns";

const PAGE_SIZE_OPTIONS = [15, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;
const APPLE_PRIVATE_RELAY_SUFFIX = "@privaterelay.appleid.com";

const formatNumber = (value) => (Number.isFinite(Number(value)) ? Number(value).toLocaleString() : "—");

function persistItem(key, value) {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        /* private mode */
    }
}

function loadStoredBool(key, fallback) {
    try {
        const stored = window.localStorage.getItem(key);
        if (stored === "true") return true;
        if (stored === "false") return false;
    } catch {
        /* private mode */
    }
    return fallback;
}

function loadStoredPage(key) {
    try {
        const stored = parseInt(window.localStorage.getItem(key), 10);
        if (Number.isFinite(stored) && stored >= 0) return stored;
    } catch {
        /* private mode */
    }
    return 0;
}

function loadStoredPageSize(key) {
    try {
        const stored = parseInt(window.localStorage.getItem(key), 10);
        if (PAGE_SIZE_OPTIONS.includes(stored)) return stored;
    } catch {
        /* private mode */
    }
    return DEFAULT_PAGE_SIZE;
}

function rowId(row) {
    return row?.id || row?.cloudUserId;
}

function isApplePrivateRelayEmail(email) {
    if (!email || typeof email !== "string") return false;
    return email.trim().toLowerCase().endsWith(APPLE_PRIVATE_RELAY_SUFFIX);
}

function emailShareKind(row) {
    const email = typeof row?.email === "string" ? row.email.trim() : "";
    if (!email || row?.hasEmail === false) return "none";
    if (row?.applePrivateRelay || isApplePrivateRelayEmail(email)) return "relay";
    return "shared";
}

/** First/last name only — never fall back to the email address. */
function promotionPersonName(row) {
    const name = typeof row?.name === "string" ? row.name.trim() : "";
    const email = typeof row?.email === "string" ? row.email.trim() : "";
    if (!name) return "—";
    if (email && name.toLowerCase() === email.toLowerCase()) return "—";
    if (name.includes("@")) return "—";
    return name;
}

function promotionEmail(row) {
    const email = typeof row?.email === "string" ? row.email.trim() : "";
    return email || "—";
}

/**
 * Shared admin Promotion tab: name/email, app, mailing, plays, first/last, duration.
 */
export default function PuzzlePromotionPanel({
    language,
    storageKeyPrefix,
    fetchUsers,
    defaultAppId,
    defaultAppName,
    emailTypes,
    emptyLabel,
    description,
    dialogHint,
}) {
    const pageStorageKey = `${storageKeyPrefix}.page`;
    const pageSizeStorageKey = `${storageKeyPrefix}.pageSize`;
    const excludeTestStorageKey = `${storageKeyPrefix}.excludeTest`;
    const excludeStaffStorageKey = `${storageKeyPrefix}.excludeStaff`;
    const excludeNoEmailStorageKey = `${storageKeyPrefix}.excludeNoEmail`;

    const navigate = useNavigate();
    const [excludeTest, setExcludeTest] = useState(() => loadStoredBool(excludeTestStorageKey, true));
    const [excludeStaff, setExcludeStaff] = useState(() => loadStoredBool(excludeStaffStorageKey, true));
    const [excludeNoEmail, setExcludeNoEmail] = useState(() => loadStoredBool(excludeNoEmailStorageKey, false));
    const [keywordInput, setKeywordInput] = useState("");
    const [keyword, setKeyword] = useState("");
    const [pageModel, setPageModel] = useState(() => ({
        page: loadStoredPage(pageStorageKey),
        pageSize: loadStoredPageSize(pageSizeStorageKey),
    }));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [sendDialogOpen, setSendDialogOpen] = useState(false);
    const [emailType, setEmailType] = useState("");

    const persistPageModel = useCallback((page, pageSize) => {
        persistItem(pageStorageKey, String(page));
        persistItem(pageSizeStorageKey, String(pageSize));
    }, [pageStorageKey, pageSizeStorageKey]);

    const applyPageModel = useCallback((next) => {
        setPageModel(next);
        persistPageModel(next.page, next.pageSize);
    }, [persistPageModel]);

    const previousLanguage = useRef(language);
    useEffect(() => {
        if (previousLanguage.current === language) return;
        previousLanguage.current = language;
        setSelectedIds([]);
        setPageModel((prev) => {
            if (prev.page === 0) return prev;
            const next = { ...prev, page: 0 };
            persistPageModel(next.page, next.pageSize);
            return next;
        });
    }, [language, persistPageModel]);

    useEffect(() => {
        const handle = window.setTimeout(() => {
            setKeyword(keywordInput.trim());
            setPageModel((prev) => {
                if (prev.page === 0) return prev;
                const next = { ...prev, page: 0 };
                persistPageModel(next.page, next.pageSize);
                return next;
            });
        }, 300);
        return () => window.clearTimeout(handle);
    }, [keywordInput, persistPageModel]);

    const resetPage = useCallback((update) => {
        update();
        setSelectedIds([]);
        setPageModel((prev) => {
            const next = prev.page === 0 ? prev : { ...prev, page: 0 };
            persistPageModel(next.page, next.pageSize);
            return next;
        });
    }, [persistPageModel]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await fetchUsers({
                    language,
                    page: pageModel.page,
                    pageSize: pageModel.pageSize,
                    includeTest: !excludeTest,
                    excludeStaff,
                    excludeNoEmail,
                    keyword,
                });
                if (!cancelled) {
                    setResult(data ?? null);
                }
            } catch (err) {
                Logger.error("Failed to load promotion users:", err);
                if (!cancelled) {
                    setError("Failed to load promotion users.");
                    setResult(null);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [language, excludeTest, excludeStaff, excludeNoEmail, keyword, pageModel.page, pageModel.pageSize, fetchUsers]);

    useEffect(() => {
        const total = Number(result?.total);
        if (!Number.isFinite(total) || total < 0) return;
        const maxPage = Math.max(0, Math.ceil(total / pageModel.pageSize) - 1);
        if (pageModel.page > maxPage) {
            applyPageModel({ page: maxPage, pageSize: pageModel.pageSize });
        }
    }, [result, pageModel.page, pageModel.pageSize, applyPageModel]);

    const rows = useMemo(() => (Array.isArray(result?.users) ? result.users : []), [result]);
    const rowCount = Number(result?.total) || 0;
    const pageIdSet = useMemo(() => new Set(rows.map((row) => rowId(row)).filter(Boolean)), [rows]);
    const pageSelectionModel = useMemo(
        () => selectedIds.filter((id) => pageIdSet.has(id)),
        [selectedIds, pageIdSet]
    );

    const handleSelectionChange = useCallback(
        (newModel) => {
            const nextPageSelected = Array.isArray(newModel) ? newModel : [];
            setSelectedIds((prev) => [
                ...prev.filter((id) => !pageIdSet.has(id)),
                ...nextPageSelected,
            ]);
        },
        [pageIdSet]
    );

    const openSendDialog = useCallback(() => {
        setEmailType("");
        setSendDialogOpen(true);
    }, []);

    const closeSendDialog = useCallback(() => {
        setSendDialogOpen(false);
    }, []);

    const handleSend = useCallback(() => {
        setSendDialogOpen(false);
        navigate(ADMIN_ADVERTISEMENT_PROMOTION_PATH);
    }, [navigate]);

    const types = Array.isArray(emailTypes) ? emailTypes : [];

    const handleDownloadCsv = useCallback(() => {
        const emailShareLabel = (kind) => {
            if (kind === "shared") return "Shared";
            if (kind === "relay") return "Apple relay";
            return "None";
        };
        const mailingLabel = (row) => {
            if (emailShareKind(row) === "none") return "";
            return row?.emailOptedOut ? "Unsubscribed" : "OK";
        };
        downloadCsv(
            `${storageKeyPrefix}-promotion.csv`,
            [
                "name",
                "email",
                "app",
                "appId",
                "emailShare",
                "mailing",
                "plays",
                "completed",
                "firstPlayedAt",
                "lastPlayedAt",
                "duration",
                "cloudUserId",
            ],
            rows.map((row) => {
                const appId = row?.app || defaultAppId;
                const appName = appId === defaultAppId ? defaultAppName : appId;
                const name = promotionPersonName(row);
                const email = promotionEmail(row);
                const firstPlayed = formatPlayTime(row?.firstPlayedAt);
                const lastPlayed = formatPlayTime(row?.lastPlayedAt);
                const duration = formatDurationMs(playDurationMs(row));
                return [
                    name === "—" ? "" : name,
                    email === "—" ? "" : email,
                    appName,
                    appId,
                    emailShareLabel(emailShareKind(row)),
                    mailingLabel(row),
                    csvNumber(row?.playCount),
                    csvNumber(row?.completedCount),
                    firstPlayed === "—" ? "" : firstPlayed,
                    lastPlayed === "—" ? "" : lastPlayed,
                    duration === "—" ? "" : duration,
                    rowId(row) || "",
                ];
            })
        );
    }, [defaultAppId, defaultAppName, rows, storageKeyPrefix]);

    const columns = useMemo(
        () => [
            {
                field: "name",
                headerName: "Name / Email",
                flex: 1.4,
                minWidth: 220,
                sortable: false,
                valueGetter: (_value, row) =>
                    [promotionPersonName(row), promotionEmail(row)].join(" "),
                renderCell: (params) => {
                    const name = promotionPersonName(params.row);
                    const email = promotionEmail(params.row);
                    const id = rowId(params.row);
                    const body = (
                        <Box sx={{ py: 0.5, lineHeight: 1.25 }}>
                            <Typography variant="body2" component="div" noWrap title={name}>
                                {name}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                component="div"
                                noWrap
                                title={email}
                            >
                                {email}
                            </Typography>
                        </Box>
                    );
                    if (!id) {
                        return body;
                    }
                    return (
                        <Box
                            onClick={(event) => {
                                event.stopPropagation();
                                navigate(`/admin/account/subscription/${id}`);
                            }}
                            sx={{
                                cursor: "pointer",
                                color: "primary.main",
                                width: "100%",
                                "& .MuiTypography-root": { color: "inherit" },
                            }}
                        >
                            {body}
                        </Box>
                    );
                },
            },
            {
                field: "app",
                headerName: "App",
                width: 180,
                sortable: false,
                valueGetter: (_value, row) => row?.app || defaultAppId,
                renderCell: (params) => {
                    const appId = params.row?.app || defaultAppId;
                    const displayName = appId === defaultAppId ? defaultAppName : appId;
                    return (
                        <Box sx={{ py: 0.5, lineHeight: 1.25 }}>
                            <Typography variant="body2" component="div" noWrap>
                                {displayName}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                component="div"
                                noWrap
                            >
                                {appId}
                            </Typography>
                        </Box>
                    );
                },
            },
            {
                field: "emailShare",
                headerName: "Email share",
                width: 150,
                sortable: false,
                valueGetter: (_value, row) => emailShareKind(row),
                renderCell: (params) => {
                    const kind = emailShareKind(params.row);
                    if (kind === "shared") {
                        return <Chip size="small" color="success" label="Shared" />;
                    }
                    if (kind === "relay") {
                        return (
                            <Tooltip
                                title="Sign in with Apple Hide My Email. The address you see is Apple’s @privaterelay.appleid.com relay, not their personal inbox. Mail may still forward if they left forwarding on."
                            >
                                <Chip size="small" color="warning" label="Apple relay" variant="outlined" />
                            </Tooltip>
                        );
                    }
                    return <Chip size="small" label="None" variant="outlined" />;
                },
            },
            {
                field: "emailOptedOut",
                headerName: "Mailing",
                width: 130,
                sortable: false,
                renderCell: (params) => {
                    if (emailShareKind(params.row) === "none") {
                        return "—";
                    }
                    return params.row?.emailOptedOut ? (
                        <Chip size="small" color="warning" label="Unsubscribed" variant="outlined" />
                    ) : (
                        <Chip size="small" color="success" label="OK" />
                    );
                },
            },
            {
                field: "playCount",
                headerName: "Plays",
                width: 100,
                sortable: false,
                valueFormatter: (value) => formatNumber(value),
            },
            {
                field: "completedCount",
                headerName: "Completed",
                width: 120,
                sortable: false,
                valueFormatter: (value) => formatNumber(value),
            },
            ...promotionPlayTimeColumns(),
        ],
        [defaultAppId, defaultAppName, navigate]
    );

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
                <TextField
                    size="small"
                    label="Search name or email"
                    value={keywordInput}
                    onChange={(event) => setKeywordInput(event.target.value)}
                    sx={{ minWidth: 240 }}
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={excludeTest}
                            onChange={(event) =>
                                resetPage(() => {
                                    const checked = event.target.checked;
                                    setExcludeTest(checked);
                                    persistItem(excludeTestStorageKey, checked ? "true" : "false");
                                })
                            }
                        />
                    }
                    label="Exclude test accounts"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={excludeStaff}
                            onChange={(event) =>
                                resetPage(() => {
                                    const checked = event.target.checked;
                                    setExcludeStaff(checked);
                                    persistItem(excludeStaffStorageKey, checked ? "true" : "false");
                                })
                            }
                        />
                    }
                    label="Exclude staff"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={excludeNoEmail}
                            onChange={(event) =>
                                resetPage(() => {
                                    const checked = event.target.checked;
                                    setExcludeNoEmail(checked);
                                    persistItem(excludeNoEmailStorageKey, checked ? "true" : "false");
                                })
                            }
                        />
                    }
                    label="Exclude no email"
                />

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
                    <CsvDownloadButton
                        label="Download promotion CSV"
                        disabled={rows.length === 0}
                        onClick={handleDownloadCsv}
                    />
                    <Button
                        variant="contained"
                        onClick={openSendDialog}
                        disabled={selectedIds.length === 0}
                        sx={{ textTransform: "none" }}
                    >
                        Send a promotion
                        {selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}
                    </Button>
                </Box>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {description}
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {!error && result && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        {formatNumber(result.total)} players
                        {selectedIds.length > 0
                            ? ` · ${formatNumber(selectedIds.length)} selected`
                            : ""}
                    </Typography>
                </Box>
            )}

            <DataGrid
                rows={rows}
                columns={columns}
                getRowId={(row) => rowId(row)}
                rowCount={rowCount}
                loading={loading}
                paginationMode="server"
                paginationModel={pageModel}
                onPaginationModelChange={applyPageModel}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                checkboxSelection
                keepNonExistentRowsSelected
                rowSelectionModel={pageSelectionModel}
                onRowSelectionModelChange={handleSelectionChange}
                autoHeight
                disableRowSelectionOnClick
                hideFooterSelectedRowCount
                localeText={{ noRowsLabel: loading ? "Loading…" : emptyLabel }}
            />

            <Dialog open={sendDialogOpen} onClose={closeSendDialog} fullWidth maxWidth="sm">
                <DialogTitle>Send a promotion</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
                        {formatNumber(selectedIds.length)} user
                        {selectedIds.length === 1 ? "" : "s"} selected
                    </Typography>

                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel id={`${storageKeyPrefix}-email-type-label`}>Promotion email type</InputLabel>
                        <Select
                            labelId={`${storageKeyPrefix}-email-type-label`}
                            label="Promotion email type"
                            value={emailType}
                            onChange={(event) => setEmailType(event.target.value)}
                            displayEmpty
                        >
                            <MenuItem value="">
                                <em>Select a type…</em>
                            </MenuItem>
                            {types.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Alert severity="info" sx={{ mb: 1 }}>
                        {dialogHint}
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeSendDialog} sx={{ textTransform: "none" }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSend}
                        sx={{ textTransform: "none" }}
                    >
                        Open Promotion
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
